import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Analysis, AnalysisProgress } from '../../interfaces/analysis';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, timer } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AnalysisService } from '../../services/analysis-tab/analysis.service';
import { POLLING_INTERVAL } from '../constants/analysis-constants';

@Component({
  selector: 'app-analysis-list-item',
  standalone: true,
  imports: [MatProgressBarModule, CommonModule, DatePipe, RouterLink],
  templateUrl: './analysis-list-item.component.html',
  styleUrl: './analysis-list-item.component.css',
})
export class AnalysisListItemComponent implements OnInit, OnDestroy {
  @Input() analysis!: Analysis;
  @Input() url?: string;
  private liveAnalysisData?: AnalysisProgress;
  private destroy$ = new Subject<void>();

  constructor(private analysisService: AnalysisService) {}

  ngOnInit(): void {
    const activeStates = ['submitted', 'analyzing'];
    const initialStatus = this.analysis.Status;
    if (activeStates.includes(initialStatus)) {
      timer(0, POLLING_INTERVAL)
        .pipe(
          switchMap(() => this.analysisService.getAnalysisProgress(this.analysis.AnalysisID)),
          takeUntil(this.destroy$)
        )
        .subscribe((progressData) => {
          if (!progressData) {
            return; //skip if no data
          }

          this.liveAnalysisData = progressData;
          const terminalStates = ['completed', 'error'];
          if (terminalStates.includes(progressData.Status.toLowerCase())) {
            //stop polling
            this.destroy$.next();
            this.destroy$.complete();
          }
        });
    }
  }
  get displayName(): string {
    return this.analysis?.AnalysisName || `Analysis #${this.analysis?.AnalysisID}`;
  }

  get progressValue(): number {
    const status = (this.liveAnalysisData?.Status || this.analysis.Status).toLowerCase();
    if (status === 'completed') {
      return 100;
    }

    return this.liveAnalysisData?.PercentComplete || this.analysis?.PercentComplete || 0;
  }

  get displayStatus(): string {
    const status = this.liveAnalysisData?.Status || this.analysis?.Status;
    if (!status) {
      return $localize`Unknown`;
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  //for getting css class
  get statusClass(): string {
    const status = this.liveAnalysisData?.Status || this.analysis?.Status;
    if (!status) {
      return $localize`unknown`;
    }
    return status.toLocaleLowerCase();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
